// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// Browsers are allowed to throttle their automatic service-worker checks.
// Long-lived Sena tabs therefore need to ask explicitly, otherwise a deploy
// may not be discovered until the user refreshes the page themselves.
const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const monitoredRegistrations = new WeakSet();
const handledWorkers = new WeakSet();
const watchedWorkers = new WeakSet();

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    const registerAfterLoad = () => {
      // One-time URL bump (fix-a-20260425) to force clients with a stale SW to
      // fetch the current registration. Do NOT bump this version on future
      // deploys — explicit update checks discover subsequent Workbox builds,
      // and activation is left to the update banner.
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js?v=fix-a-20260425`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    };
    // A cached/lazily executed bundle may start after the load event. Waiting
    // for an event that already fired leaves that tab without update checks.
    if (document.readyState === 'complete') registerAfterLoad();
    else window.addEventListener('load', registerAfterLoad, { once: true });
  }
}

export function registerValidSW(swUrl, config) {
  // updateViaCache: 'none' makes the browser bypass its HTTP cache when
  // checking the SW script for updates. Without this a long CDN/browser TTL
  // on /service-worker.js can hide new versions for hours.
  return navigator.serviceWorker
    .register(swUrl, { updateViaCache: config?.updateViaCache || 'none' })
    .then((registration) => {
      const watchInstallingWorker = () => {
        const installingWorker = registration.installing;
        if (installingWorker) {
          watchWorker(registration, installingWorker, config);
        }
      };

      if (typeof registration.addEventListener === 'function') {
        registration.addEventListener('updatefound', watchInstallingWorker);
      } else {
        registration.onupdatefound = watchInstallingWorker;
      }

      // updatefound may have fired before register() resolved, or a previous
      // page may already have left the new worker waiting. Surface both states
      // immediately; onUpdate is discovery only and must not auto-activate it.
      if (registration.waiting) {
        handleInstalledWorker(registration, registration.waiting, config);
      }
      watchInstallingWorker();
      monitorForUpdates(registration, config);
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
      // Offline cold starts can fail registration before monitoring exists.
      // Reconnect must retry registration as well as checking known workers.
      window.addEventListener('online', () => registerValidSW(swUrl, config), { once: true });
    });
}

function watchWorker(registration, worker, config) {
  if (handledWorkers.has(worker) || watchedWorkers.has(worker)) return;

  if (worker.state === 'installed') {
    handleInstalledWorker(registration, worker, config);
    return;
  }

  watchedWorkers.add(worker);

  const handleStateChange = () => {
    if (worker.state === 'installed') {
      handleInstalledWorker(registration, worker, config);
    }
  };

  if (typeof worker.addEventListener === 'function') {
    worker.addEventListener('statechange', handleStateChange);
  } else {
    worker.onstatechange = handleStateChange;
  }
}

function handleInstalledWorker(registration, worker, config) {
  if (handledWorkers.has(worker)) return;
  handledWorkers.add(worker);

  if (navigator.serviceWorker.controller) {
    console.log('New content is available. Waiting for the user to apply it.');
    config?.onUpdate?.(registration, worker);
  } else {
    console.log('Content is cached for offline use.');
    config?.onSuccess?.(registration);
  }
}

function monitorForUpdates(registration, config) {
  if (monitoredRegistrations.has(registration)) return;
  monitoredRegistrations.add(registration);

  let lastCheckAt = Date.now();
  let checkInFlight = false;

  const checkForUpdate = async ({ force = false } = {}) => {
    if (document.visibilityState === 'hidden') return;
    if (checkInFlight) return;
    if (!force && Date.now() - lastCheckAt < UPDATE_CHECK_INTERVAL_MS) return;

    lastCheckAt = Date.now();
    checkInFlight = true;
    try {
      await registration.update();
      // Some browsers do not reliably deliver updatefound to every open tab.
      // Inspect waiting/installing so the banner can still be shown. This must
      // never activate the worker or reload the page by itself.
      if (registration.waiting) {
        handleInstalledWorker(registration, registration.waiting, config);
      } else if (registration.installing) {
        watchWorker(registration, registration.installing, config);
      }
    } catch (error) {
      // An offline update check is expected to fail and should not affect the app.
      if (navigator.onLine !== false) {
        console.warn('Unable to check for a Sena update:', error);
      }
    } finally {
      checkInFlight = false;
    }
  };

  const checkWhenVisible = () => {
    if (document.visibilityState === 'visible') checkForUpdate();
  };
  const checkWhenOnline = () => checkForUpdate({ force: true });

  window.addEventListener('focus', checkForUpdate);
  window.addEventListener('online', checkWhenOnline);
  document.addEventListener('visibilitychange', checkWhenVisible);
  window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
  void checkForUpdate({ force: true });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
